import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IdexService } from 'app/idex.service';
import { Toast } from 'app/shared/toast/toast';
import { Observable } from 'rxjs';
import { FileUploader } from './file-uploader';

@Injectable({
  providedIn: 'root'
})
export class OnedriveUploaderService {

   private chunkSize = 1048576;
   busy: boolean;
   pathFolder: string;
   access_token: string;

   constructor(private translate: TranslateService, private service: IdexService, private zone: NgZone, private toast: Toast, private fileUploader: FileUploader) { }

   uploadFiles(access_token: any, files: any, callback: any, folderId?: string): void {
      this.busy = true;
      if (files.length === 0) {
         this.busy = false;
         return;
      }
      this.zone.run(() => { callback.init(files); });
      let index = 0;
      let folderTree = [];
      const uploadNextFile = async () => {
         if (index === files.length) {
            this.busy = false;
            this.zone.run(() => { callback.complete(); });
            return;
         }
         const file: any = await this.uploadFolderProcess(folderTree, files[index++]);
         if (file.canceled) {
            this.zone.run(() => { callback.cancelUpload(index - 1, 'Cancel'); });
            uploadNextFile();
            return;
         }
         if (/[/\\:*?"<>|]/.test(file.name)) {
            this.translate.get('alert.create_folder_error').subscribe(s=>{
               this.toast.error(s);});
            this.zone.run(() => { callback.specialCharacter(index - 1); });
            uploadNextFile();
            return;
         }
         this.init(access_token, file.uploadFolderId, file.name, file.size).subscribe(result => {
            let offset = 0;
            const xhr = new XMLHttpRequest();
            const send = () => {
               const end = Math.min(offset + this.chunkSize, file.size);
               xhr.open('PUT', result.uploadUrl, true);
               xhr.setRequestHeader('Content-Range', 'bytes ' + offset + '-' + (end - 1) + '/' + file.size);
               const blob = file.slice.call(file, offset, end);
               xhr.send(blob);
            };
            xhr.onload = () => {
               if (xhr.status === 202) {
                  if (file.canceled) {
                     this.zone.run(() => {
                        callback.cancelUpload(index - 1, 'Cancel');
                     });
                     uploadNextFile();
                     return;
                  }
                  const progress = Math.floor(offset * 100 / file.size);
                  this.zone.run(() => { callback.progress(index - 1, progress); });
                  offset += this.chunkSize;
                  send();
               } else if (xhr.status === 201) {
                  this.zone.run(() => { callback.progress(index - 1, 100); });
                  uploadNextFile();
               } else {
                  file.canceled = true;
                  this.zone.run(() => { callback.cancelUpload(index - 1, 'Internal Server Error'); });
                  uploadNextFile();
               }
            };
            send();
         }, error => {
            file.error = error;
            this.zone.run(() => { callback.cancelUpload(index - 1, error); });
            uploadNextFile();
         });
      };
      uploadNextFile();
   }

   private init(access_token, folderId, fileName, maxFileSize): Observable<any> {
      return this.service.uploadFileToOnedrive(access_token, folderId, fileName, maxFileSize)
   }

   process(access_token, dataTransfer: DataTransfer, callback: any, pathFolder): void {
      this.busy = true;
      this.access_token = access_token;
      this.pathFolder = pathFolder ? pathFolder : 'root';
      if (dataTransfer.items) {
         const item = dataTransfer.items[0];
         if (item.webkitGetAsEntry) {
            const list = [];
            const dataTransferItem = dataTransfer.items;
            for (let i = 0; i < dataTransferItem.length; i++) {
               list.push(dataTransferItem[i].webkitGetAsEntry());
            }
            this.fileUploader.getFilesAsync(list).then(files => {
               this.uploadFiles(access_token, files, callback);
            }, () => {
               this.uploadFiles(access_token, dataTransfer.files, callback);
            });
         } else {
            this.uploadFiles(access_token, dataTransfer.files, callback);
         }
      } else {
         this.uploadFiles(access_token, dataTransfer.files, callback);
      }
   }

   uploadFolder(access_token, list: FileList, callback: any, pathFolder): void {
      this.busy = true;
      this.pathFolder = pathFolder ? pathFolder : 'root';
      this.access_token = access_token;
      const files = [];
      for (let i = 0; i < list.length; i++) {
         const file: any = list.item(i);
         file.fullPath = this.trimName(file.webkitRelativePath);
         files.push(file);
      }
      this.uploadFiles(access_token, files, callback);
   }

   trimName(fullPath: string): string | null {
      const resultArray: string[] = fullPath.split('/').map(item => item.trim());
      const mergedString: string = resultArray.join('/');
      return mergedString;
   }

   async createFolderStructure(folderNames: string[], parentId, nestedFolder, folderTree): Promise<string> {
      let currentParentId = parentId;
      let i = 0;
      for (const folderName of folderNames) {
         if(folderTree.length > 0){
            let folderTreeIndex = folderTree.findIndex(item => item[i]?.name === folderName);
            if(folderTreeIndex >= 0){
               currentParentId = folderTree[folderTreeIndex][i].id;
               nestedFolder.push({name: folderName, id: currentParentId});
               i++;
               continue;
            } else {
               currentParentId = await this.createOrFindFolder(folderName, currentParentId);
               nestedFolder.push({name: folderName, id: currentParentId});
               i++;
            }
         } else {
            currentParentId = await this.createOrFindFolder(folderName, currentParentId);
            nestedFolder.push({name: folderName, id: currentParentId});
         }
      }

      return currentParentId;
   }

   async createOrFindFolder(folderName: string, parentId: string): Promise<string> {
      let createResponse = await this.service.onedrive_createFolder(this.access_token, folderName, parentId, 'rename').toPromise();
      return createResponse.id;
   }

   async uploadFolderProcess(folderTree, file): Promise<any> {
      let nestedFolder = [];
      if(!file.webkitRelativePath && !file.fullPath){
         if(!file.uploadFolderId){
            file.uploadFolderId = 'root';
         }
         return file;
      }
      let folderNameArray = file.webkitRelativePath ? file.webkitRelativePath.split('/') : file.fullPath.split('/');
      if (folderNameArray[0] == ''){
         folderNameArray.pop();
         folderNameArray.shift();
      } else {
         folderNameArray.pop();
      }
      const lastFolderId = await this.createFolderStructure(folderNameArray, this.pathFolder, nestedFolder, folderTree);
      file.uploadFolderId = lastFolderId;
      folderTree.push(nestedFolder);
      return file;
   }
}
